import "bootstrap-icons/font/bootstrap-icons.css";
import { Link } from "react-router-dom";
import "./productCategory.css";

import { Badge, Button, Dropdown, Menu, Table, Modal, Form, InputNumber, DatePicker, Space, Tag, Input, Popover } from "antd";
import { EditOutlined, FilterOutlined, PlusOutlined, HistoryOutlined } from "@ant-design/icons";
import moment from "moment";
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadAllProductCurrency } from "../../redux/actions/productCurrency/getProductCurrencyAction";
import axios from "axios";
import { toast } from "react-toastify";

const FILTER_STYLE = { marginBottom: 12 };
const FILTER_LABEL = { display: "block", marginBottom: 4, color: "rgba(0,0,0,0.65)", fontSize: 12 };

function CustomTable({ list, total, addProductCurrencyButton, uploadButton }) {
  const dispatch = useDispatch();
  const [columnItems, setColumnItems] = useState([]);
  const [columnsToShow, setColumnsToShow] = useState([]);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState({ name: "", symbol: "" });
  const [tableScrollY, setTableScrollY] = useState(400);
  const tableWrapRef = useRef(null);
  const [rateModalVisible, setRateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [currencyRates, setCurrencyRates] = useState([]);
  const [rateForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const columns = [
    {
      title: "S.No",
      key: "sNo",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, { id }) => (
        <Link to={`/product-currency/${id}`}>{name}</Link>
      ),
    },
    {
      title: "Symbol",
      dataIndex: "symbol",
      key: "symbol",
      render: (symbol, { id }) => (
        <Link to={`/product-currency/${id}`}>{symbol}</Link>
      ),
    },
    {
      title: "Conversion Rate",
      dataIndex: "conversion",
      key: "conversion",
      render: (conversion, { id }) => (
        <Link to={`/product-currency/${id}`}>{conversion}</Link>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => moment(createdAt).format("YYYY-MM-DD"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditCurrency(record)}
            size="small"
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={<HistoryOutlined />}
            onClick={() => handleManageRates(record)}
            size="small"
          >
            Rates
          </Button>
        </Space>
      ),
    },
  ];

  const menuItems = columns.map((item) => ({
    key: item.key,
    label: <span>{item.title}</span>,
  }));

  useEffect(() => {
    setColumnItems(menuItems);
    setColumnsToShow(columns);
  }, []);

  useEffect(() => {
    if (!tableWrapRef.current) return;
    const el = tableWrapRef.current;
    const ro = new ResizeObserver(() => {
      setTableScrollY(Math.max(200, el.clientHeight - 88));
    });
    ro.observe(el);
    setTableScrollY(Math.max(200, el.clientHeight - 88));
    return () => ro.disconnect();
  }, [list]);

  const colVisibilityClickHandler = (col) => {
    const ifColFound = columnsToShow.find((item) => item.key === col.key);
    if (ifColFound) {
      const filteredColumnsToShow = columnsToShow.filter(
        (item) => item.key !== col.key
      );
      setColumnsToShow(filteredColumnsToShow);
    } else {
      const foundIndex = columns.findIndex((item) => item.key === col.key);
      const foundCol = columns.find((item) => item.key === col.key);
      let updatedColumnsToShow = [...columnsToShow];
      updatedColumnsToShow.splice(foundIndex, 0, foundCol);
      setColumnsToShow(updatedColumnsToShow);
    }
  };

  const addKeys = (arr) => arr.map((i) => ({ ...i, key: i.id }));
  const getListLimit = () => {
    // Mobile: fetch more rows so card view doesn't look empty (endpoint uses pagination).
    if (typeof window === "undefined") return 10;
    return window.innerWidth < 992 ? 1000 : 10;
  };

  const activeFilterCount = [filters.name, filters.symbol].filter(Boolean).length;
  const nameLower = (filters.name || "").trim().toLowerCase();
  const symbolLower = (filters.symbol || "").trim().toLowerCase();
  const filteredData = list && (nameLower || symbolLower)
    ? list.filter((item) => {
        if (nameLower && !(item.name || "").toLowerCase().includes(nameLower)) return false;
        if (symbolLower && !(item.symbol || "").toLowerCase().includes(symbolLower)) return false;
        return true;
      })
    : list;

  const filterContent = (
    <div style={{ width: 260 }}>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Name</label>
        <Input
          placeholder="Filter by name"
          value={filters.name}
          onChange={(e) => setFilters((f) => ({ ...f, name: e.target.value }))}
          allowClear
        />
      </div>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Symbol</label>
        <Input
          placeholder="Filter by symbol"
          value={filters.symbol}
          onChange={(e) => setFilters((f) => ({ ...f, symbol: e.target.value }))}
          allowClear
        />
      </div>
      <div style={{ textAlign: "center", marginTop: 8 }}>
        <Button size="small" onClick={() => setFilters({ name: "", symbol: "" })}>
          Clear All Filters
        </Button>
      </div>
    </div>
  );

  // Handle edit currency
  const handleEditCurrency = (currency) => {
    setSelectedCurrency(currency);
    editForm.setFieldsValue({
      name: currency.name,
      symbol: currency.symbol,
    });
    setEditModalVisible(true);
  };

  // Handle manage rates
  const handleManageRates = async (currency) => {
    setSelectedCurrency(currency);
    try {
      const response = await axios.get(`product-currency/${currency.id}/rates`);
      setCurrencyRates(response.data || []);
      rateForm.resetFields();
      setRateModalVisible(true);
    } catch (error) {
      console.error('Error fetching rates:', error);
      setCurrencyRates([]);
      setRateModalVisible(true);
    }
  };

  // Handle add new rate
  const handleAddRate = async () => {
    try {
      const values = await rateForm.validateFields();
      await axios.post(`product-currency/${selectedCurrency.id}/rates`, {
        conversion: values.conversion,
        effective_from_date: values.effective_from_date.format('YYYY-MM-DD'),
      });
      toast.success('Conversion rate added successfully');
      rateForm.resetFields();
      // Reload rates
      const response = await axios.get(`product-currency/${selectedCurrency.id}/rates`);
      setCurrencyRates(response.data || []);
      // Reload currency list
      dispatch(loadAllProductCurrency({ page: 1, limit: getListLimit() }));
    } catch (error) {
      console.error('Error adding rate:', error);
      toast.error('Failed to add conversion rate');
    }
  };

  // Handle update currency
  const handleUpdateCurrency = async () => {
    try {
      const values = await editForm.validateFields();
      await axios.put(`product-Currency/${selectedCurrency.id}`, values);
      toast.success('Currency updated successfully');
      setEditModalVisible(false);
      dispatch(loadAllProductCurrency({ page: 1, limit: getListLimit() }));
    } catch (error) {
      console.error('Error updating currency:', error);
      toast.error('Failed to update currency');
    }
  };

  return (
    <>
      <div className="card column-design list-page-fill-viewport">
        <div className="card-body">
          <div className="card-title d-flex justify-content-between align-items-center list-page-header" style={{ marginBottom: 0 }}>
            <h5 className="mb-0">Product Currency List</h5>
            <div className="pcurr-header-actions">
              {list && (
                <>
                  <Popover
                    content={filterContent}
                    title="Filters"
                    trigger="click"
                    open={filterVisible}
                    onOpenChange={setFilterVisible}
                    placement="bottomLeft"
                  >
                    <Badge count={activeFilterCount} size="small" offset={[-2, 2]}>
                      <Button
                        type="primary"
                        className="filter-icon-btn"
                        icon={<FilterOutlined />}
                        onClick={() => setFilterVisible((v) => !v)}
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    </Badge>
                  </Popover>
                  <Dropdown
                    overlay={<Menu onClick={colVisibilityClickHandler} items={columnItems} />}
                    placement="bottomRight"
                  >
                    <Button className="column-visibility">Column Visibility</Button>
                  </Dropdown>
                </>
              )}
              {addProductCurrencyButton}
              {uploadButton}
            </div>
          </div>

          <div className="list-page-table-container">
            <div className="list-page-table-inner">
              <div ref={tableWrapRef} className="list-page-table-wrap">
                {/* Desktop Table */}
                <div className="d-none d-lg-block">
                  <Table
                    scroll={{ x: true, y: tableScrollY }}
                    loading={!list}
                    pagination={{
                      defaultPageSize: 10,
                      pageSizeOptions: [10, 20, 50, 100, 200],
                      showSizeChanger: true,
                      onChange: (page, limit) => {
                        dispatch(loadAllProductCurrency({ page, limit }));
                      },
                    }}
                    columns={columnsToShow}
                    dataSource={filteredData ? addKeys(filteredData) : []}
                  />
                </div>

                {/* Mobile Cards */}
                <div className="d-lg-none pcurr-mobile-cards">
                  {filteredData && filteredData.length > 0 ? (
                    <div className="pcurr-mobile-list">
                      {filteredData.map((record, idx) => (
                        <div key={record.id ?? idx} className="pcurr-mobile-card">
                          <div className="pcurr-mobile-card-body">
                            <div className="pcurr-mobile-row">
                              <span className="pcurr-mobile-sno">{idx + 1}</span>
                              <div className="pcurr-mobile-main">
                                <div className="pcurr-mobile-title">
                                  <Link to={`/product-currency/${record.id}`}>
                                    {record.name || "—"}
                                  </Link>
                                </div>
                                <div className="pcurr-mobile-grid">
                                  <div className="pcurr-mobile-item">
                                    <span className="pcurr-mobile-label">Symbol</span>
                                    <span className="pcurr-mobile-value">
                                      {record.symbol || "—"}
                                    </span>
                                  </div>
                                  <div className="pcurr-mobile-item">
                                    <span className="pcurr-mobile-label">Conversion</span>
                                    <span className="pcurr-mobile-value">
                                      {record.conversion ?? "—"}
                                    </span>
                                  </div>
                                  <div className="pcurr-mobile-item">
                                    <span className="pcurr-mobile-label">Created</span>
                                    <span className="pcurr-mobile-value">
                                      {record.createdAt
                                        ? moment(record.createdAt).format("YYYY-MM-DD")
                                        : "—"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="pcurr-mobile-actions">
                              <Button
                                size="small"
                                type="primary"
                                onClick={() => handleEditCurrency(record)}
                                className="pcurr-mobile-btn"
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                onClick={() => handleManageRates(record)}
                                className="pcurr-mobile-btn"
                              >
                                Rates
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <p className="text-muted mb-0">No product currencies found</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rate Management Modal */}
      <Modal
        title={`Manage Conversion Rates - ${selectedCurrency?.name || ''} (${selectedCurrency?.symbol || ''})`}
        open={rateModalVisible}
        onCancel={() => {
          setRateModalVisible(false);
          setSelectedCurrency(null);
          setCurrencyRates([]);
          rateForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        <div style={{ marginBottom: '20px' }}>
          <h4>Add New Rate</h4>
          <Form
            form={rateForm}
            layout="inline"
            onFinish={handleAddRate}
          >
            <Form.Item
              name="conversion"
              label="Conversion Rate"
              rules={[{ required: true, message: 'Please enter conversion rate' }]}
            >
              <InputNumber
                min={0}
                step={0.01}
                precision={4}
                placeholder="Rate"
                style={{ width: '150px' }}
              />
            </Form.Item>
            <Form.Item
              name="effective_from_date"
              label="Effective From"
              rules={[{ required: true, message: 'Please select effective date' }]}
            >
              <DatePicker
                format="DD-MMM-YYYY"
                placeholder="Select date"
                style={{ width: '200px' }}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                Add Rate
              </Button>
            </Form.Item>
          </Form>
        </div>

        <div>
          <h4>Rate History</h4>
          <Table
            dataSource={currencyRates.map((rate, index) => ({ ...rate, key: rate.id || index }))}
            columns={[
              {
                title: 'Conversion Rate',
                dataIndex: 'conversion',
                key: 'conversion',
                render: (rate) => <Tag color="blue">{rate}</Tag>,
              },
              {
                title: 'Effective From',
                dataIndex: 'effective_from_date',
                key: 'effective_from_date',
                render: (date) => moment(date).format('DD-MMM-YYYY'),
              },
              {
                title: 'Created At',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (date) => moment(date).format('DD-MMM-YYYY HH:mm'),
              },
            ]}
            pagination={false}
            size="small"
          />
        </div>
      </Modal>

      {/* Edit Currency Modal */}
      <Modal
        title="Edit Currency"
        open={editModalVisible}
        onOk={handleUpdateCurrency}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedCurrency(null);
          editForm.resetFields();
        }}
        width={500}
      >
        <Form
          form={editForm}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Currency Name"
            rules={[{ required: true, message: 'Please enter currency name' }]}
          >
            <Input placeholder="Enter currency name" />
          </Form.Item>
          <Form.Item
            name="symbol"
            label="Symbol"
            rules={[{ required: true, message: 'Please enter currency symbol' }]}
          >
            <Input placeholder="Enter currency symbol" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

const GetAllProductCurrency = ({ refreshTrigger, addProductCurrencyButton, uploadButton }) => {
  const dispatch = useDispatch();
  const list = useSelector((state) => state.productCurrencies?.list);

  useEffect(() => {
    const limit =
      typeof window === "undefined" ? 10 : window.innerWidth < 992 ? 1000 : 10;
    dispatch(loadAllProductCurrency({ page: 1, limit }));
  }, [dispatch, refreshTrigger]);

  return (
    <CustomTable
      list={list}
      addProductCurrencyButton={addProductCurrencyButton}
      uploadButton={uploadButton}
    />
  );
};

export default GetAllProductCurrency;
